import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import { type CSSProperties, useRef } from 'react';

import { timeBottomAxisContainer } from './TimeseriesTimeBottomAxisArea.sty.js';
import { TimeBottomAxis } from '../../../../core/components/axes/components/TimeBottomAxis.js';
import { ChartGrid } from '../../../../core/components/chart-grid/ChartGridRoot.js';
import { KEYBOARD_HANDLERS } from '../../../../core/components/zoom/constants.js';
import { cursorStyle } from '../../../../core/components/zoom/ZoomCursors.sty.js';
import { isTouchableDevice } from '../../../../core/utils/is-touchable-device.js';
import { TIMESERIES_TIME_AXIS_ARIA_LABEL } from '../../../constants.js';
import { useZoomState } from '../../../hooks/store/selectors.js';
import { useChartInteractions } from '../../../hooks/useChartInteractions.js';
import { useTimeseriesTickAxis } from '../../../hooks/useTimeseriesTickAxis.js';
import { useXAxisConfig } from '../../../hooks/useXAxisConfig.js';
import { useXScales } from '../../../hooks/useXScale.js';
import { AREAS } from '../../../types/timeseries-grid-area-rules.js';
import { useApplyPanDelta } from '../../overlays/hooks/useApplyPanDelta.js';
import { TimeseriesPanningControls } from '../TimeseriesPanningControls.js';

/**
 * The bottom axis component with default values already set
 */
export function TimeseriesTimeBottomAxisArea() {
  const xAxisConfig = useXAxisConfig();
  const { currentScale: xScale } = useXScales();
  const { pan } = useChartInteractions();
  const { zoomOutEnabled } = useZoomState();
  const isPanEnabled = pan?.enabled && zoomOutEnabled;
  const tickAxis = useTimeseriesTickAxis();
  const bottomTickAxis = tickAxis?.bottom || { ticks: [], formattedTicks: [] };
  const applyPan = useApplyPanDelta();

  const axisRef = useRef<HTMLDivElement>(null);

  useGesture(
    {
      onDrag: (e) =>
        !KEYBOARD_HANDLERS.includes(e.type) && applyPan(-e.delta[0]),
    },
    {
      target: axisRef,
      eventOptions: { passive: false },
      drag: {
        filterTaps: !isTouchableDevice(),
        pointer: { buttons: [1, 4] },
        enabled: isPanEnabled,
      },
    },
  );

  const style: CSSProperties = isTouchableDevice()
    ? { touchAction: 'none' }
    : {};

  if (!bottomTickAxis.ticks.length || !xScale) {
    return null;
  }

  return (
    <ChartGrid.GridArea gridArea={AREAS.bottomAxis} style={style}>
      {({ availableWidth, availableHeight }) => (
        <div
          ref={axisRef}
          className={clsx(
            cursorStyle({ isPanEnabled }),
            timeBottomAxisContainer({ isTouchableDevice: isTouchableDevice() }),
          )}
          role="group"
          aria-label={TIMESERIES_TIME_AXIS_ARIA_LABEL}
        >
          {isPanEnabled && (
            <TimeseriesPanningControls onClickPan={(d) => applyPan(-d)} />
          )}
          <TimeBottomAxis
            ticks={bottomTickAxis.ticks}
            formattedTicks={bottomTickAxis.formattedTicks}
            scale={xScale}
            containerHeight={availableHeight}
            containerWidth={availableWidth}
            label={xAxisConfig?.label}
            shrinkTickEdges={isPanEnabled}
          />
        </div>
      )}
    </ChartGrid.GridArea>
  );
}

TimeseriesTimeBottomAxisArea['displayName'] = 'TimeseriesValueBottomAxisArea';
